// BReels AI Studio — text→image, text→video, and stitched movie mode.
// Uses Emergent LLM key on backend (no user-facing credit cost for Studio-tier users).
// Also hosts a "Upload from device" mode that mirrors StoriesUploader — device
// file picker + client-side ≤600s validation + optional caption + optional
// background audio → POST /posts tagged as kind:"reel" source:"device_upload".
import { useEffect, useRef, useState } from "react";
import { Sparkles, ImageIcon, Video, Clapperboard, Loader2, Download, Play, AlertTriangle, Upload, Music, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import ComingSoon from "../components/ComingSoon";

const MODES = [
  { id: "image", label: "Poster", icon: ImageIcon,      desc: "Single AI still (Nano Banana / GPT-Image-1)" },
  { id: "video", label: "Clip",   icon: Video,          desc: "Single Sora-2 video, up to 12s" },
  { id: "movie", label: "Movie",  icon: Clapperboard,   desc: "2-5 min stitched movie (Claude splits → Sora-2 → ffmpeg)" },
  { id: "upload", label: "Upload", icon: Upload,        desc: "Upload your own video/photo from device (≤2 min)" },
];

// Curated free background tracks — hosted by the platform. Users can also skip and keep the video's own audio.
const AUDIO_TRACKS = [
  { id: "none",    label: "Keep original audio", url: null },
  { id: "lofi",    label: "Lo-fi Chill",         url: "https://cdn.pixabay.com/audio/2022/02/22/audio_e58cf13b90.mp3" },
  { id: "epic",    label: "Epic Cinematic",      url: "https://cdn.pixabay.com/audio/2022/10/30/audio_347111d5e0.mp3" },
  { id: "gamer",   label: "Gamer Trap",          url: "https://cdn.pixabay.com/audio/2023/06/30/audio_a3c99ee20e.mp3" },
];

const readDataUrl = (f) => new Promise((res) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
});

// Probes the video's duration client-side without uploading anything. Returns
// seconds (float) or rejects on decode error. Used to enforce the 600s cap
// BEFORE the file makes the trip over the network.
const probeVideoDuration = (file) => new Promise((res, rej) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => {
    const d = v.duration;
    URL.revokeObjectURL(url);
    res(d);
  };
  v.onerror = () => { URL.revokeObjectURL(url); rej(new Error("Could not read this video")); };
  v.src = url;
});

export default function BReelsStudio() {
  // ----------------------------------------------------------------------
  // Oct 2026 — BReels is on hold while we spin it out as its own project.
  // The entire implementation below (_BReelsStudioImpl) is preserved
  // intact — backend endpoints /api/breels/* also stay fully wired — so
  // flipping this back on is a one-line change when BReels v2 lands.
  // ----------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-nexus-void text-white px-4 py-20 flex items-center justify-center">
      <div className="w-full max-w-3xl">
        <ComingSoon
          title="BReels is loading"
          subtitle="Our AI short-film engine is being rebuilt as its own flagship project. The library stays live, but new reels pause here until BReels v2 ships."
          accent="violet"
          icon={Clapperboard}
          testId="breels-coming-soon"
        />
      </div>
    </div>
  );
}

function _BReelsStudioImpl() {
  const { user } = useAuth();
  const [mode, setMode] = useState("image");
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("cinematic");
  const [duration, setDuration] = useState(8);
  const [size, setSize] = useState("1280x720");
  const [scenes, setScenes] = useState(10);
  const [sps, setSps] = useState(12);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [movieJob, setMovieJob] = useState(null);
  const [genError, setGenError] = useState(null);
  const pollRef = useRef(null);
  // Device-upload state (kept separate from AI state so the two flows never interfere)
  const uploadFileRef = useRef(null);
  const [uploadPick, setUploadPick] = useState(null);      // {file, isVideo, preview, duration}
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploadAudio, setUploadAudio] = useState("none");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  // Guards against double/triple-submit from rapid repeat clicks or a laggy tap
  // registering more than once -- disabled={uploadBusy} on the button only takes
  // effect once React re-renders, so a burst of clicks/taps arriving before that
  // re-render commits can all sneak past it and each independently upload the file
  // and create a post. A ref updates synchronously, so this catches it regardless.
  const uploadSubmittingRef = useRef(false);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const pickUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file after clearing
    if (!f) return;
    if (f.size > 1_000_000_000) { toast.error("Reel max 1000MB"); return; }
    const isVideo = f.type.startsWith("video/");
    let duration = 0;
    if (isVideo) {
      try { duration = await probeVideoDuration(f); }
      catch { toast.error("Could not read this video"); return; }
      if (duration > 600.5) { toast.error(`Reels are capped at 10 minutes — this one is ${Math.round(duration)}s`); return; }
    }
    const preview = await readDataUrl(f);
    setUploadPick({ file: f, isVideo, preview, duration });
  };

  const submitUpload = async () => {
    if (!user) return;
    if (!uploadPick) { toast.error("Pick a video or photo first"); return; }
    if (uploadSubmittingRef.current) return;
    uploadSubmittingRef.current = true;
    setUploadBusy(true);
    setUploadPct(0);
    try {
      // Always upload to a real file, big or small — never inline base64 into
      // the post. media_url ends up in every feed/profile response with no
      // projection to exclude it, so a base64 blob there bloats every page
      // load for anyone who ever sees this reel in their feed.
      const fd = new FormData(); fd.append("file", uploadPick.file);
      const endpoint = uploadPick.isVideo ? "/upload/video" : "/upload/image";
      // Real progress feedback -- large files (up to 1000MB) can take a while,
      // and an unlabeled spinner with no visible progress reads as "stuck" long
      // before it actually is. This also makes a genuinely hung upload (e.g. an
      // infra-level timeout on a very slow connection) obvious rather than
      // silently indistinguishable from a normal, still-progressing upload.
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const media_url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      const audioTrack = AUDIO_TRACKS.find((t) => t.id === uploadAudio);
      await api.post("/posts/v2", {
        content: uploadCaption.trim() || (uploadPick.isVideo ? "🎬 Reel" : "🖼️ Reel"),
        kind: "reel",
        media_url,
        source: "device_upload",
        background_audio_url: audioTrack?.url || null,
        duration_seconds: uploadPick.isVideo ? Math.round(uploadPick.duration) : null,
      });
      toast.success("Reel posted to your feed");
      setUploadPick(null); setUploadCaption(""); setUploadAudio("none");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post reel");
    }
    uploadSubmittingRef.current = false;
    setUploadBusy(false);
  };

  const submit = async () => {
    if (!title.trim() || !prompt.trim()) { toast.error("Title and prompt required"); return; }
    setBusy(true); setResult(null); setMovieJob(null); setGenError(null);
    try {
      if (mode === "image") {
        const { data } = await api.post("/breels/v2/image", { title, prompt, style });
        setResult({ kind: "image", ...data });
        toast.success("Poster generated");
      } else if (mode === "video") {
        const { data } = await api.post("/breels/v2/video", { title, prompt, duration, size });
        // Video is now async — poll like movie
        toast.info(`Sora-2 rendering… ~${Math.ceil(data.estimated_duration_sec / 60)} min`);
        setMovieJob({ ...data, kind: "video" });
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const { data: st } = await api.get(`/breels/v2/status/${data.job_id}`);
            setMovieJob({ ...st, kind: "video" });
            if (st.status === "done" || st.status === "failed" || st.status === "cancelled") {
              clearInterval(pollRef.current);
              pollRef.current = null;
              if (st.status === "done") {
                setResult({ kind: "video", video_url: st.output_url, title, ...(st.doc || {}) });
                toast.success("Clip rendered");
              }
              if (st.status === "failed") {
                const msg = st.error || "Sora-2 render failed";
                setGenError(msg);
                toast.error(msg);
              }
            }
          } catch (e) {
            // A 404 means the job is gone (e.g. the server restarted mid-render) --
            // stop polling and tell the user, instead of retrying forever with no feedback.
            if (e?.response?.status === 404) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              const msg = "Generation was interrupted (server restarted) -- please try again";
              setGenError(msg);
              toast.error(msg);
            }
          }
        }, 5000);
      } else {
        const { data } = await api.post("/breels/v2/movie", { title, prompt, scenes, seconds_per_scene: sps, size });
        setMovieJob(data);
        toast.info(`Movie job queued — ~${Math.ceil((scenes * sps) / 60)} min of content`);
        // Poll status
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const { data: st } = await api.get(`/breels/v2/status/${data.job_id}`);
            setMovieJob(st);
            if (st.status === "done" || st.status === "failed" || st.status === "cancelled") {
              clearInterval(pollRef.current);
              pollRef.current = null;
              if (st.status === "done") { setResult({ kind: "video", video_url: st.output_url, title }); toast.success("Movie ready"); }
              if (st.status === "failed") {
                const msg = st.error || "Movie job failed";
                setGenError(msg);
                toast.error(msg);
              }
            }
          } catch (e) {
            if (e?.response?.status === 404) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              const msg = "Generation was interrupted (server restarted) -- please try again";
              setGenError(msg);
              toast.error(msg);
            }
          }
        }, 5000);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Generation failed";
      setGenError(msg);
      toast.error(msg);
    }
    setBusy(false);
  };

  if (!user) return <div className="p-8 text-center text-white/60 text-sm">Sign in to use BReels AI.</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-4 px-4 pt-4 pb-12" data-testid="breels-studio">
      <Toaster theme="dark" position="top-center" />
      <div className="label-eyebrow text-nexus-cyan flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" />// breels ai studio · text → image · video · movie</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Direct your own scene.</h1>
      <p className="text-sm text-white/60">Type a prompt. Pick a mode. NEXUS renders it with the models below — no credit cost for Studio-tier creators.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {MODES.map(({ id, label, icon: Icon, desc }) => (
          <button
            key={id}
            onClick={() => setMode(id)}
            data-testid={`breels-mode-${id}`}
            className={`rounded-2xl border p-3 text-left transition-colors ${
              mode === id ? "border-nexus-cyan bg-nexus-cyan/5" : "border-white/10 hover:border-white/25"
            }`}
          >
            <Icon className={`w-4 h-4 ${mode === id ? "text-nexus-cyan" : "text-white/60"}`} />
            <div className="mt-1 text-sm font-display font-bold">{label}</div>
            <div className="text-[10px] text-white/50 leading-snug">{desc}</div>
          </button>
        ))}
      </div>

      {mode === "upload" ? (
        <div className="space-y-3 border border-white/10 rounded-2xl p-4 bg-black/30" data-testid="breels-upload-panel">
          <div className="text-[11px] tracking-[0.3em] uppercase font-display text-white/60">// upload from device</div>
          <input type="file" ref={uploadFileRef} accept="video/*,image/*" className="hidden" onChange={pickUpload} data-testid="reel-upload-input" />
          {!uploadPick ? (
            <button
              onClick={() => uploadFileRef.current?.click()}
              data-testid="reel-upload-pick"
              className="w-full aspect-[9/16] sm:aspect-video rounded-2xl border-2 border-dashed border-white/15 hover:border-nexus-cyan/70 transition-colors flex flex-col items-center justify-center gap-2 text-white/50 hover:text-white"
            >
              <Upload className="w-8 h-8" />
              <span className="text-sm font-display tracking-wider">Pick a photo or video ≤ 2 min</span>
              <span className="text-[10px] text-white/40">MP4 / MOV / WEBM · PNG / JPG · up to 1000MB</span>
            </button>
          ) : (
            <div className="space-y-3">
              <div className="relative rounded-2xl overflow-hidden bg-black aspect-[9/16] max-h-[60vh]" data-testid="reel-upload-preview">
                {uploadPick.isVideo
                  ? <video src={uploadPick.preview} controls playsInline className="w-full h-full object-contain" />
                  : <img src={uploadPick.preview} alt="" className="w-full h-full object-contain" />}
                <button
                  onClick={() => setUploadPick(null)}
                  className="absolute top-2 right-2 bg-black/60 hover:bg-nexus-crimson w-8 h-8 rounded-full flex items-center justify-center text-white"
                  data-testid="reel-upload-clear"
                  aria-label="Remove picked file"
                ><X className="w-4 h-4" /></button>
                {uploadPick.isVideo && (
                  <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">
                    {Math.round(uploadPick.duration)}s / 600s
                  </div>
                )}
              </div>

              <input
                value={uploadCaption}
                onChange={(e) => setUploadCaption(e.target.value)}
                placeholder="Add a caption or on-screen text (optional)"
                maxLength={140}
                className="w-full bg-transparent border border-white/15 rounded-full px-4 py-2 text-sm"
                data-testid="reel-upload-caption"
              />

              {uploadPick.isVideo && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-white/50"><Music className="w-3 h-3" />Background audio</div>
                  <div className="flex flex-wrap gap-1.5">
                    {AUDIO_TRACKS.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setUploadAudio(t.id)}
                        data-testid={`reel-audio-${t.id}`}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                          uploadAudio === t.id ? "border-nexus-cyan text-nexus-cyan bg-nexus-cyan/10" : "border-white/15 text-white/60 hover:border-white/40"
                        }`}
                      >{t.label}</button>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={submitUpload}
                disabled={uploadBusy}
                data-testid="reel-upload-submit"
                className="btn-nexus w-full disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploadBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> {uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Posting…"}</> : <><Upload className="w-4 h-4" /> Post reel</>}
              </button>
              <p className="text-[10px] text-white/40 leading-relaxed">
                Uploaded reels are tagged <span className="text-nexus-cyan">source=device_upload</span> so they stay
                separate from your AI-generated BReels in the library and feed.
              </p>
            </div>
          )}
        </div>
      ) : (
      <div className="space-y-2 border border-white/10 rounded-2xl p-4 bg-black/30">
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (e.g. 'Warzone: Last Stand')" maxLength={80}
          className="w-full bg-transparent border border-white/15 rounded-full px-4 py-2 text-sm"
          data-testid="breels-title"
        />
        <textarea
          value={prompt} onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          placeholder="Describe the scene, mood, camera. What should happen?"
          className="w-full bg-transparent border border-white/15 rounded-2xl px-4 py-2 text-sm"
          data-testid="breels-prompt"
        />

        {mode === "image" && (
          <div className="flex flex-wrap gap-1.5">
            {["cinematic", "anime", "pixel-art", "photoreal", "cyberpunk"].map((s) => (
              <button key={s} onClick={() => setStyle(s)} data-testid={`breels-style-${s}`}
                className={`px-3 py-1 rounded-full text-[11px] border ${style === s ? "bg-nexus-cyan/15 border-nexus-cyan text-nexus-cyan" : "border-white/15 text-white/70"}`}>{s}</button>
            ))}
          </div>
        )}

        {mode === "video" && (
          <div className="flex flex-wrap gap-2 items-center text-xs">
            <label className="text-white/50">Duration:</label>
            {[4, 8, 12].map((d) => (
              <button key={d} onClick={() => setDuration(d)} data-testid={`breels-duration-${d}`}
                className={`px-3 py-1 rounded-full border ${duration === d ? "bg-nexus-cyan/15 border-nexus-cyan text-nexus-cyan" : "border-white/15 text-white/70"}`}>{d}s</button>
            ))}
            <label className="text-white/50 ml-2">Size:</label>
            {[["1280x720", "16:9"], ["1024x1792", "9:16"], ["1024x1024", "1:1"]].map(([v, l]) => (
              <button key={v} onClick={() => setSize(v)} data-testid={`breels-size-${v}`}
                className={`px-3 py-1 rounded-full border ${size === v ? "bg-nexus-cyan/15 border-nexus-cyan text-nexus-cyan" : "border-white/15 text-white/70"}`}>{l}</button>
            ))}
          </div>
        )}

        {mode === "movie" && (
          <div className="flex flex-wrap gap-2 items-center text-xs">
            <label className="text-white/50">Scenes:</label>
            <input type="number" min="5" max="25" value={scenes} onChange={(e) => setScenes(Math.max(5, Math.min(25, +e.target.value)))} className="w-16 bg-transparent border border-white/15 rounded-full px-3 py-1 text-center" data-testid="breels-scenes" />
            <label className="text-white/50">Seconds/scene:</label>
            {[4, 8, 12].map((d) => (
              <button key={d} onClick={() => setSps(d)} data-testid={`breels-sps-${d}`}
                className={`px-3 py-1 rounded-full border ${sps === d ? "bg-nexus-cyan/15 border-nexus-cyan text-nexus-cyan" : "border-white/15 text-white/70"}`}>{d}s</button>
            ))}
            <div className="text-white/50">≈ Movie length: <span className="text-nexus-gold">{Math.ceil((scenes * sps) / 60)} min {(scenes * sps) % 60}s</span></div>
          </div>
        )}
      </div>
      )}

      {mode !== "upload" && (
      <button onClick={submit} disabled={busy || (movieJob && movieJob.status !== "done" && movieJob.status !== "failed")}
        className="btn-nexus w-full justify-center" data-testid="breels-submit">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate {mode}</>}
      </button>
      )}

      {genError && (
        <div className="rounded-2xl border border-nexus-pink/40 bg-nexus-pink/10 p-4 text-sm flex items-start gap-3" data-testid="breels-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-nexus-pink flex-shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-nexus-pink">// generation failed</div>
            <div className="text-white/80 mt-1 break-words whitespace-pre-wrap" data-testid="breels-error-message">{genError}</div>
            <button onClick={() => setGenError(null)} data-testid="breels-error-dismiss"
              className="text-[11px] text-white/50 hover:text-white mt-2 underline">dismiss</button>
          </div>
        </div>
      )}

      {movieJob && movieJob.status !== "done" && (
        <div className="rounded-2xl border border-nexus-cyan/30 bg-nexus-cyan/5 p-4 text-sm" data-testid="movie-progress">
          <div className="font-display font-bold">// {movieJob.kind === "video" ? "sora-2 clip" : "movie"} job · {movieJob.status}</div>
          {movieJob.progress && (
            <div className="mt-2">
              Rendered {movieJob.progress.done}/{movieJob.progress.total} scenes
              <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-nexus-cyan transition-all" style={{ width: `${(movieJob.progress.done / movieJob.progress.total) * 100}%` }} />
              </div>
            </div>
          )}
          {movieJob.error && <div className="text-nexus-pink mt-2">{movieJob.error}</div>}
        </div>
      )}

      {result && (
        <div className="rounded-2xl border border-nexus-cyan/30 bg-black/30 p-4 space-y-3" data-testid="breels-result">
          <div className="text-sm text-nexus-cyan font-display">// result</div>
          {result.kind === "image" && result.thumbnail && (
            <img src={result.thumbnail} alt={result.title} className="w-full rounded-xl" data-testid="result-image" />
          )}
          {result.kind === "video" && result.video_url && (
            <video src={(process.env.REACT_APP_BACKEND_URL || "") + result.video_url}
                   controls className="w-full rounded-xl" data-testid="result-video" />
          )}
          <div className="flex gap-2">
            {result.video_url && (
              <a href={(process.env.REACT_APP_BACKEND_URL || "") + result.video_url} download className="btn-ghost inline-flex items-center gap-1"><Download className="w-3 h-3" /> Download</a>
            )}
            <Play className="w-4 h-4 text-white/40" />
            <span className="text-xs text-white/50">Saved to your BReels library</span>
          </div>
        </div>
      )}
    </div>
  );
}
